'use client'
import { logger } from '@/lib/logger';

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { createSWS_Error, ErrorType, ErrorSeverity } from '@/lib/error-handling'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorId?: string
}

class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false
    }
  }

  static getDerivedStateFromError(error: Error): State {
    const errorId = `global-error-${Date.now()}`
    return {
      hasError: true,
      error,
      errorId
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Create a critical SWS error for global failures
    const swsError = createSWS_Error(
      ErrorType.UNKNOWN,
      ErrorSeverity.CRITICAL,
      `Global Application Error: ${error.message}`,
      {
        originalError: error,
        context: {
          componentStack: errorInfo.componentStack,
          errorBoundary: 'GlobalErrorBoundary',
          timestamp: new Date().toISOString()
        },
        recoverable: false
      }
    )

    // Log critical error
    logger.group('🚨 GLOBAL APPLICATION ERROR 🚨')
    logger.error('Critical Error:', swsError)
    logger.error('Component Stack:', errorInfo.componentStack)
    logger.groupEnd()

    // Could send to error reporting service
    // reportCriticalError(swsError)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-red-400 text-8xl mb-8" aria-hidden="true">💥</div>

            <h1 className="text-4xl font-bold text-white mb-4">
              Something went terribly wrong
            </h1>

            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              The application encountered a critical error and cannot continue safely.
              This might be due to a browser compatibility issue, memory problem, or unexpected system failure.
            </p>

            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6 mb-8">
              <h3 className="text-red-300 font-semibold mb-3">Error Details</h3>
              <p className="text-red-200/80 text-sm font-mono">
                {this.state.error?.message || 'Unknown error occurred'}
              </p>
              {this.state.errorId && (
                <p className="text-red-200/60 text-xs mt-2 font-mono">
                  Error ID: {this.state.errorId}
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={this.handleReload}
                className="px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-cyan-500/25"
              >
                🔄 Reload Application
              </button>

              <button
                onClick={() => window.history.back()}
                className="px-8 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors"
              >
                ← Go Back
              </button>
            </div>

            <div className="mt-8 text-slate-500 text-sm">
              <p>If this problem persists, try:</p>
              <ul className="mt-2 space-y-1 text-left max-w-md mx-auto">
                <li>• Clearing your browser cache</li>
                <li>• Using a different browser</li>
                <li>• Checking your internet connection</li>
                <li>• Restarting your browser</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default GlobalErrorBoundary